import React, { useState, useEffect } from "react";
import { VscUnverified } from "react-icons/vsc";
import { MdOutlineSendToMobile } from "react-icons/md";
import {
  AiOutlineLoading3Quarters,
} from "react-icons/ai";
import BasicBVN from "../layout/BasicBVN";
import AdvancedBVNSlip from "../layout/AdvancedBVN";
import CryptoJS from "crypto-js";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { config } from "../../config/config.jsx";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useBVNSlip } from "../../context/BVNSlipContext";
import BVNVerificationHistoryTable from "./History/BVNVerificationHistory.jsx";
import LogoImg from "../../assets/images/Logo.png";

function BVNVerify() {
  /* ---------------------------- component state ---------------------------- */
  const [selectedVerify, setSelectedVerify] = useState("bvn"); // fixed to bvn
  const [selectedSlip, setSelectedSlip] = useState(""); // unselected by default
  const [bvn, setBvn] = useState("");
  const [phone, setPhone] = useState("");
  const [showSlip, setShowSlip] = useState(false);
  const [loading, setLoading] = useState(false);
  const [apiData, setApiData] = useState(null); // <-- Add this state
  const [apiPrices, setApiPrices] = useState(null);
  const [cardSlip, setCardSlip] = useState([
    { label: "Basic Details", value: "Basic", price: 1 },
    { label: "Advanced Details", value: "Advanced", price: 1 },
  ]);

  const navigate = useNavigate();
  const { viewSlip } = useBVNSlip();

  // Add your secret key for decryption
  const SECRET_KEY = import.meta.env.VITE_APP_SECRET_KEY;

  function decryptData(ciphertext) {
    if (!ciphertext) return null;
    try {
      const bytes = CryptoJS.AES.decrypt(ciphertext, SECRET_KEY);
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decrypted);
    } catch {
      return null;
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedVerify || !selectedSlip) {
      toast.error("Please select verification type and details needed");
      return;
    }

    if (selectedVerify === "bvn" && (!bvn || bvn.length !== 11)) {
      toast.error("Please enter a valid 11-digit BVN");
      return;
    }



    // Find the selected slip's price from the updated prices
    const selectedSlipObj =
      apiPrices?.find((s) => s.value === selectedSlip) ||
      cardSlip.find((s) => s.value === selectedSlip);
    const slipAmount = selectedSlipObj ? selectedSlipObj.price : 0;

    // Show confirmation dialog
    const result = await Swal.fire({
      title: "Confirm Verification",
      text: `Are you sure you want to proceed with this verification? ${
        slipAmount > 0 ? `Amount: ₦${slipAmount}` : ""
      }`,
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#f59e0b",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, verify",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) {
      return;
    }

    // Get userId from encrypted localStorage
    let userId = null;
    try {
      const userStr = localStorage.getItem("user");
      if (userStr) {
        const userObj = decryptData(userStr);
        userId = userObj?._id || userObj?.id;
      }
    } catch {}

    const payload = {
      verifyWith: selectedVerify,
      slipLayout: selectedSlip,
      amount: slipAmount,
      userId,
    };

    if (selectedVerify === "bvn") {
      payload.bvn = bvn;
    }
     // Debug log
    setLoading(true);
    try {
      const response = await axios.post(
        `${config.apiBaseUrl}${config.endpoints.BVNVerify}`,
        payload,
        { withCredentials: true }
      );
       // Debug log
      // Store the verification data in context
      viewSlip(response.data?.data?.data, selectedSlip);

      // Show success alert
      await Swal.fire({
        title: "Verification Successful!",
        text: "Your BVN has been successfully verified.",
        icon: "success",
        confirmButtonColor: "#f59e0b",
      });

      // Navigate to appropriate slip view
      if (selectedSlip === "Basic") {
        navigate("/dashboard/verifications/basicbvn");
      } else {
        navigate("/dashboard/verifications/advancedbvn");
      }
    } catch (error) {
      
      toast.error(
        error.response?.data?.message ||
          "Verification failed. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchPrices = async () => {
      try {
        const response = await axios.get(
          `${config.apiBaseUrl}${config.endpoints.currentapipricing}`,
          { withCredentials: true }
        );
         // Debug log
        // Find BVN pricing
        const bvnPricing = response.data.find((item) => item.key === "bvn");

        if (bvnPricing) {
          // Update cardSlip with new prices
          setCardSlip([
            {
              label: "Basic Details",
              value: "Basic",
              price: bvnPricing.prices?.agent,
            },
            {
              label: "Advanced Details",
              value: "Advanced",
              price: bvnPricing.prices?.agent,
            },
          ]);
        }
      } catch (error) {
        
        toast.error("Failed to fetch current prices");
      }
    };

    fetchPrices();
  }, []);

  /* --------------------------------- render -------------------------------- */
  return (
    <div className="w-full">
      <div className="rounded-2xl mb-10 bg-white p-6 shadow-lg border border-gray-100">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-gray-800">BVN Verification</h2>
          <p className="text-sm text-gray-500 mt-1">Verify your Bank Verification Number</p>
        </div>
        <form action="#" method="post" onSubmit={handleSubmit}>
          <p className="mt-7 text-[14px] font-medium text-gray-600 mb-2">1. Details Needed</p>
          <div className="grid gap-6 p-4 sm:grid-cols-2 md:grid-cols-2">
          {cardSlip.map(({ label, value, price }) => (
            <label
              key={value}
              className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border p-6 text-center transition
            ${
              selectedSlip === value
                ? "border-sky-400 ring-2 ring-sky-300 shadow-lg"
                : "border-gray-200 shadow-md hover:shadow-lg"
            }`}
            >
              <input
                type="radio"
                name="slipLayout"
                value={value}
                checked={selectedSlip === value}
                onChange={() => setSelectedSlip(value)}
                className="hidden"
                required
              />

              {/* price */}
              <p className="mb-4 text-3xl font-bold tracking-wide text-slate-700">
                ₦{price}.00
              </p>

              {/* label */}
              <h4 className="mb-4 text-base font-semibold text-gray-600">
                {label}
              </h4>

              {/* visual radio indicator */}
              <span
                className={`flex h-6 w-6 items-center justify-center rounded-full border-2 ${
                  selectedSlip === value ? "border-sky-400" : "border-gray-300"
                }`}
              >
                {selectedSlip === value && (
                  <span className="h-2.5 w-2.5 rounded-full bg-sky-500"></span>
                )}
              </span>
            </label>
          ))}
        </div>
          <p className="mt-7 text-[14px] font-medium text-gray-600 mb-2">
            2. Supply Verification Details
          </p>
          <div className="flex flex-row justify-start gap-3 items-center my-5">
            <p className="bg-purple-700 p-2 rounded-full text-white">
              <VscUnverified className="text-3xl" />
            </p>
            <div>
              <p className="text-gray-500 text-[16px] mb-1 ">For a watch</p>
              <p className="text-gray-500 text-[16px]">
                Dail *565*0# from your registered phone number to get your BVN!
              </p>
            </div>
          </div>
            <input
              type="text"
              className="pl-4 py-3 border border-gray-300 focus:border-sky-500 focus:ring-1 focus:ring-sky-500 rounded-lg w-full outline-none transition-all"
              placeholder="BVN NUMBER"
              required
              name="bvn"
              id="bvn"
              inputMode="numeric"
              autoComplete="off"
              pattern="\d{11}"
              maxLength="11"
              title="BVN must be exactly 11 digits"
              value={bvn}
              onChange={(e) => setBvn(e.target.value.replace(/\D/, ""))}
            />


          <p className="text-gray-400 text-xs mt-3 flex items-center gap-1">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
            We'll never share your details with anyone else.
          </p>
        <label className="flex items-start mt-8 space-x-3 cursor-pointer">
          <span className="relative">
            <input
              type="checkbox"
              className="peer shrink-0 appearance-none h-5 w-5 border border-gray-400 rounded-sm bg-white checked:bg-blue-600 checked:border-transparent focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              required
              title="Required"
            />
            <svg
              className="absolute w-4 h-4 text-white left-0.5 top-0.5 pointer-events-none hidden peer-checked:block"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M16.707 5.293a1 1 0 010 1.414L8.414 15l-4.121-4.121a1 1 0 011.414-1.414L8.414 12.586l7.879-7.879a1 1 0 011.414 0z"
                clipRule="evenodd"
              />
            </svg>
          </span>
          <span className="text-sm text-gray-400">
            By checking this box, you agreed that the owner of the ID has
            granted you consent to verify his/her identity.
          </span>
        </label>

        <button
          type="submit"
          disabled={loading}
          className={`flex items-center text-xl mt-10 mb-8 cursor-pointer justify-center gap-2 ${
            loading ? "bg-gray-400" : "bg-sky-500 hover:bg-sky-600"
          } text-white font-medium py-2 px-4 rounded-xl w-full h-[50px] transition-colors`}
        >
          {loading ? (
            <AiOutlineLoading3Quarters className="animate-spin" />
          ) : (
            <MdOutlineSendToMobile className="" />
          )}
          {loading ? "Verifying..." : "Verify"}
        </button>
        </form>
        <ToastContainer />
      </div>

      {/* History Table */}
      <div className="w-full mt-10">
        <BVNVerificationHistoryTable />
      </div>
    </div>
  );
}

export default BVNVerify;
